/**
 * ManualFlightReportPage.js
 * 
 * A modern, aviation-themed Manual Flight Report submission page for FDXVA.
 * Features card-based design, accessible forms, dark mode support, and
 * comprehensive modal feedback for submission results.
 * 
 * Features:
 * - Sequential tab indexing (top-to-bottom, left-to-right)
 * - Aircraft selector with unique colors and icons per aircraft type
 * - ICAO code inputs (4 char limit, centered, uppercase)
 * - Hours (0-23) and Minutes (0-59) dropdown selectors
 * - Pre-populated but editable VATSIM ID field
 * - Detailed success/error modal with flight report data
 * - Full dark mode support via props.darkmode
 * - Responsive layout for desktop and mobile
 * - WCAG 2.1 AA accessibility compliance
 * 
 * @component
 * @author FDXVA Development Team
 * @version 2.0
 */

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

import TopNav from "../components/TopNav";
import "./ManualFlightReportPage.css";

/**
 * Aircraft configuration with icons, colors, and display names
 * Each aircraft type has a unique visual identifier
 */
const AIRCRAFT_CONFIG = {
    "": { icon: "🛫", name: "Select Aircraft", color: "default" },
    "B77F": { icon: "✈️", name: "Boeing 777-F", color: "#3b82f6" },
    "B732": { icon: "🛩️", name: "Boeing 737-200", color: "#8b5cf6" },
    "B74F": { icon: "🦅", name: "Boeing 747-F", color: "#ec4899" },
    "B752": { icon: "🚀", name: "Boeing 757", color: "#06b6d4" },
    "B76F": { icon: "⚡", name: "Boeing 767-F", color: "#10b981" },
    "C208": { icon: "🌲", name: "Cessna Caravan 208", color: "#f59e0b" },
    "MD11": { icon: "🔥", name: "McDonnell Douglas MD-11", color: "#ef4444" },
    "AT72": { icon: "🌀", name: "ATR-72", color: "#84cc16" },
    "B722": { icon: "💫", name: "Boeing 727", color: "#6366f1" },
    "A306": { icon: "🌟", name: "Airbus A300-600", color: "#14b8a6" },
    "DC10": { icon: "☄️", name: "McDonnell Douglas DC-10", color: "#f97316" },
    "FA20": { icon: "💎", name: "Dassault Falcon 20", color: "#a855f7" },
    "A310": { icon: "🎯", name: "Airbus A310", color: "#22c55e" },
    "AT42": { icon: "🍃", name: "ATR-42", color: "#eab308" },
    "DC87": { icon: "👑", name: "Douglas DC-8", color: "#e11d48" }
};

/**
 * Generate hours array (0-23) for dropdown
 * @returns {number[]} Array of hour values
 */
//const generateHours = () => Array.from({ length: 24 }, (_, i) => i);

/**
 * Generate minutes array (0-59) for dropdown
 * @returns {number[]} Array of minute values
 */
//const generateMinutes = () => Array.from({ length: 60 }, (_, i) => i);

/**
 * ManualFlightReportPage Component
 * 
 * @param {Object} props - Component props
 * @param {string} props.page - Current page name for navigation
 * @param {boolean} props.darkmode - Dark mode toggle
 * @param {string} props.cid - User's VATSIM CID (pre-populated)
 * @returns {JSX.Element} The rendered component
 */
const ManualFlightReportPage = (props) => {
    const navigate = useNavigate();

    // State management - DO NOT MODIFY (per requirements)
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalType, setModalType] = useState(null);
    const [modalMessage, setModalMessage] = useState("");
    
    // Additional state for storing full response data
    const [responseData, setResponseData] = useState(null);
    
    // Form field state for controlled components
    const [vatsimValue, setVatsimValue] = useState(props.cid || "");
    const [selectedAircraft, setSelectedAircraft] = useState("");

    useEffect(() => {
        console.log(props);
        // Update VATSIM value if props change
        if (props.cid) {
            setVatsimValue(props.cid);
        }
        return () => {};
    }, [props]);

    /**
     * Form submission handler - DO NOT MODIFY LOGIC
     * Only UI/UX changes per requirements
     * @param {Event} e - Form submission event
     */
    function handleForm(e) {
        e.preventDefault();

        const orig = e.target.elements.orig.value;
        const dest = e.target.elements.dest.value;
        const eqpt = e.target.elements.eqpt.value;
        const vatsim = e.target.elements.vatsim.value;
        const remarks = e.target.elements.remarks.value;

        const obj = {
            orig: orig,
            dest: dest,
            eqpt: eqpt,
            vatsim: vatsim,
            remarks: remarks
        };
        postFlightReport(obj);
    }

    /**
     * API submission handler - DO NOT MODIFY LOGIC
     * Only modal display changes per requirements
     * @param {Object} obj - Form data object
     */
    const postFlightReport = async (obj) => {
        setLoading(true);
        setError(null);

        console.log(obj);

        // Create FormData for proper POST request
        const formDataParams = new URLSearchParams();

        formDataParams.append('orig', obj.orig);
        formDataParams.append('dest', obj.dest);
        formDataParams.append('eqpt', obj.eqpt);
        formDataParams.append('hours', obj.hours);
        formDataParams.append('mins', obj.mins);
        formDataParams.append('vatsim', obj.vatsim);
        formDataParams.append('remarks', obj.remarks);

        try {
            const { data } = await axios.post(
                "/api/manual_flight_report",
                formDataParams,
                {
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
                }
            );

            if (data) {
                console.log(data);

                // Store full response data for modal display
                setResponseData(data);
                
                // Determine success or error based on response message
                if (data.mesg && data.mesg.includes("received")) {  //actually: '-request-received-'
                    setModalType("success");
                    setModalMessage(data.mesg);
                } else {
                    setModalType("error");
                    setModalMessage(data.mesg);
                }
                setIsModalOpen(true);
            } else {
                setError("No response from server");
                setModalType("error");
                setModalMessage("Failed to submit manual flight report. No response from server.");
                setIsModalOpen(true);
            }
        } catch (err) {
            console.error("API Error:", err);
            const errorMessage = err.response?.data?.message || 
                "A flight report is already pending for this user. A new flight report cannot be submitted until the current one is processed.";
            setError(errorMessage);
            setModalType("error");
            setModalMessage(err.response?.data?.mesg || errorMessage);
            setResponseData(err.response?.data || null);
            setIsModalOpen(true);
        } finally {
            setLoading(false);
        }
    };

    /**
     * Handle modal close and navigation to Operations page
     */
    const handleModalClose = () => {
        setIsModalOpen(false);
        navigate('/operations', { state: { refresh: true } });
    };


    /**
     * Format currency for display
     * @param {number} value - Numeric value
     * @returns {string} Formatted currency string
     */
    // const formatCurrency = (value) => {
    //     return new Intl.NumberFormat('en-US', {
    //         style: 'currency',
    //         currency: 'USD',
    //         minimumFractionDigits: 0,
    //         maximumFractionDigits: 0
    //     }).format(value);
    // };

    return (
        <div className={`mfr-page ${props.darkmode ? 'dark' : ''}`}>
            <TopNav page={props.page} darkMode={props.darkmode} />
            {error && <div className="error-message">{error}</div>}
            <main className="mfr-container" role="main" aria-labelledby="mfr-title">
                {/* Page Header */}
                <header className="mfr-header">
                    <div className="mfr-header-content">

                        <h1 id="mfr-title">Manual Flight Report</h1>
                        <p className="mfr-header-subtitle">
                            Submit your VATSIM flight for review and approval
                        </p>
                    </div>
                </header>

                {/* Main Form Card */}
                <div className="mfr-form-card">
                    <form className="mfr-form" onSubmit={handleForm} aria-describedby="form-instructions">
                        
                        {/* Section 1: Aircraft Selection */}
                        <section className="mfr-section" aria-labelledby="aircraft-section">
                            <div className="mfr-section-header">
                                <div className="mfr-section-icon" role="img" aria-hidden="true">🛫</div>
                                <h2 className="mfr-section-title" id="aircraft-section">Aircraft Selection</h2>
                            </div>
                            <div className="mfr-aircraft-wrapper">
                                <label htmlFor="eqpt" className="visually-hidden">Select Aircraft Type</label>
                                <select
                                    id="eqpt"
                                    name="eqpt"
                                    className="mfr-aircraft-select"
                                    required
                                    tabIndex={1}
                                    value={selectedAircraft}
                                    onChange={(e) => setSelectedAircraft(e.target.value)}
                                    style={selectedAircraft && AIRCRAFT_CONFIG[selectedAircraft] ? {
                                        borderColor: AIRCRAFT_CONFIG[selectedAircraft].color,
                                        boxShadow: `0 0 0 2px ${AIRCRAFT_CONFIG[selectedAircraft].color}20`
                                    } : {}}
                                    aria-required="true"
                                >
                                    <option value="">🛫 Select Aircraft Type</option>
                                    <option value="B77F" >Boeing 777-F</option>
                                    <option value="B732" >Boeing 737-200</option>
                                    <option value="B74F" >Boeing 747-F</option>
                                    <option value="B752" >Boeing 757</option>
                                    <option value="B76F" >Boeing 767-F</option>
                                    <option value="C208" >Cessna Caravan 208</option>
                                    <option value="MD11" >McDonnell Douglas MD-11</option>
                                    <option value="AT72" >ATR-72</option>
                                    <option value="B722" >Boeing 727</option>
                                    <option value="A306" >Airbus A300-600</option>
                                    <option value="DC10" >McDonnell Douglas DC-10</option>
                                    <option value="FA20" >Dassault Falcon 20</option>
                                    <option value="A310" >Airbus A310</option>
                                    <option value="AT42" >ATR-42</option>
                                    <option value="DC87" >Douglas DC-8</option>
                                </select>
                            </div>
                        </section>

                        {/* Section 2: Route Information */}
                        <section className="mfr-section" aria-labelledby="route-section">
                            <div className="mfr-section-header">
                                <div className="mfr-section-icon" role="img" aria-hidden="true">🗺️</div>
                                <h2 className="mfr-section-title" id="route-section">Flight Route</h2>
                            </div>
                            <div className="mfr-route-grid">
                                <div className="mfr-route-field">
                                    <label htmlFor="orig" className="mfr-route-label">Origin (ICAO)</label>
                                    <input
                                        type="text"
                                        id="orig"
                                        name="orig"
                                        className="mfr-icao-input"
                                        placeholder="KMEM"
                                        maxLength={4}
                                        required
                                        tabIndex={2}
                                        autoComplete="off"
                                        aria-required="true"
                                        aria-describedby="orig-hint"
                                    />
                                    <span id="orig-hint" className="visually-hidden">Enter 4-character ICAO airport code</span>
                                </div>
                                
                                <div className="mfr-route-divider" role="img" aria-label="to">
                                
                                </div>
                                
                                <div className="mfr-route-field">
                                    <label htmlFor="dest" className="mfr-route-label">Destination (ICAO)</label>
                                    <input
                                        type="text"
                                        id="dest"
                                        name="dest"
                                        className="mfr-icao-input"
                                        placeholder="KJFK"
                                        maxLength={4}
                                        required
                                        tabIndex={3}
                                        autoComplete="off"
                                        aria-required="true"
                                        aria-describedby="dest-hint"
                                    />
                                    <span id="dest-hint" className="visually-hidden">Enter 4-character ICAO airport code</span>
                                </div>
                            </div>
                        </section>

                        {/* Section 3: Flight Time */}
                        {/* <section className="mfr-section" aria-labelledby="time-section">
                            <div className="mfr-section-header">
                                <div className="mfr-section-icon" role="img" aria-hidden="true">⏱️</div>
                                <h2 className="mfr-section-title" id="time-section">Flight Duration</h2>
                            </div>
                            <div className="mfr-time-grid">
                                <div className="mfr-time-field">
                                    <label htmlFor="hours" className="mfr-time-label">
                                        <span className="mfr-time-label-icon">🕐</span>
                                        Hours
                                    </label>
                                    <select
                                        id="hours"
                                        name="hours"
                                        className="mfr-time-select"
                                        required
                                        tabIndex={4}
                                        defaultValue=""
                                        aria-required="true"
                                    >
                                        <option value="" disabled>--</option>
                                        {generateHours().map((hour) => (
                                            <option key={hour} value={hour}>
                                                {hour.toString().padStart(2, '0')}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                
                                <div className="mfr-time-field">
                                    <label htmlFor="mins" className="mfr-time-label">
                                        <span className="mfr-time-label-icon">⏰</span>
                                        Minutes
                                    </label>
                                    <select
                                        id="mins"
                                        name="mins"
                                        className="mfr-time-select"
                                        required
                                        tabIndex={5}
                                        defaultValue=""
                                        aria-required="true"
                                    >
                                        <option value="" disabled>--</option>
                                        {generateMinutes().map((min) => (
                                            <option key={min} value={min}>
                                                {min.toString().padStart(2, '0')}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </section> */}

                        {/* Section 4: VATSIM Verification */}
                        <section className="mfr-section" aria-labelledby="vatsim-section">
                            <div className="mfr-section-header">
                                <div className="mfr-section-icon" role="img" aria-hidden="true">🌐</div>
                                <h2 className="mfr-section-title" id="vatsim-section">Evidence of Flight Operations</h2>
                            </div>
                            
                            <div className="mfr-vatsim-wrapper">
                                <div className="mfr-vatsim-field">
                                    <label htmlFor="vatsim" className="mfr-vatsim-label">
                                        <span role="img" aria-hidden="true">🆔</span>
                                        VATSIM CID
                                    </label>
                                    <input
                                        type="text"
                                        id="vatsim"
                                        name="vatsim"
                                        className="mfr-vatsim-input"
                                        value={vatsimValue}
                                        onChange={(e) => setVatsimValue(e.target.value)}
                                        tabIndex={6}
                                        aria-describedby="vatsim-notice"
                                    />
                                </div>
                            </div>

                            <div className="mfr-notice" role="alert" id="vatsim-notice">
                                <span className="mfr-notice-icon" role="img" aria-hidden="true">⚠️</span>
                                <p className="mfr-notice-text">
                                    A flight report that is submitted with this form can only be
                                    accepted if the flight was flown online
                                    using the VATSIM ID on record at the time of the flight. 
                                    The remarks section of your flight plan should also include the text, 
                                    <i>FedEx Express Virtual - FDXVA.ORG</i>, or simply <i>FDXVA.ORG</i>.
                                </p>
                            </div>
                        </section>

                        {/* Section 5: Remarks */}
                        <section className="mfr-section" aria-labelledby="remarks-section">
                            <div className="mfr-section-header">
                                <div className="mfr-section-icon" role="img" aria-hidden="true">📝</div>
                                <h2 className="mfr-section-title" id="remarks-section">Reason for Manual Report</h2>
                            </div>
                            
                            {/* <label htmlFor="remarks" className="mfr-remarks-label">
                                Enter the reason why you did not use ACARS:
                            </label> */}
                            <textarea
                                id="remarks"
                                name="remarks"
                                className="mfr-remarks-textarea"
                                placeholder="Please explain why you are submitting a manual flight report instead of using ACARS (e.g., technical issues, simulator crash, etc.)"
                                required
                                tabIndex={7}
                                aria-required="true"
                            />
                        </section>

                        {/* Submit Section */}
                        <div className="mfr-submit-section">
                            <button
                                type="submit"
                                className={`mfr-submit-btn ${loading ? 'loading' : ''}`}
                                disabled={loading}
                                tabIndex={8}
                                aria-busy={loading}
                            >
                                {loading ? (
                                    <>
                                        <span className="mfr-spinner" aria-hidden="true"></span>
                                        <span>Submitting Report...</span>
                                    </>
                                ) : (
                                    <>
                                        <span className="mfr-submit-btn-icon" role="img" aria-hidden="true">📤</span>
                                        <span>Submit Flight Report</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </main>

            {/* Results Modal */}
            {isModalOpen && (
                <div 
                    className="mfr-modal-overlay" 
                    role="dialog" 
                    aria-modal="true" 
                    aria-labelledby="modal-title"
                    onClick={(e) => e.target === e.currentTarget && handleModalClose()}
                >
                    <div className="mfr-modal">
                        {/* Modal Header */}
                        <div className={`mfr-modal-header ${modalType}`}>
                            <span className="mfr-modal-header-icon" role="img" aria-hidden="true">
                                {modalType === 'success' ? '✅' : '❌'}
                            </span>
                            <h2 className="mfr-modal-title" id="modal-title">
                                {modalType === 'success' ? 'Report Submitted!' : 'Submission Failed'}
                            </h2>
                        </div>

                        {/* Modal Body */}
                        <div className="mfr-modal-body">
                            <p className="mfr-modal-message">{modalMessage}</p>

                            {/* Success details */}
                            {modalType === 'success' && responseData && (
                                <>
                                    {/* Flight Details */}
                                    {responseData.postData && (
                                        <div className="mfr-modal-details">
                                            <h3 className="mfr-modal-details-title">
                                                <span role="img" aria-hidden="true">✈️</span>
                                                Flight Details
                                            </h3>
                                            <div className="mfr-modal-details-grid">
                                                <div className="mfr-modal-detail-item">
                                                    <div className="mfr-modal-detail-label">Route</div>
                                                    <div className="mfr-modal-detail-value">
                                                        {responseData.postData.orig} → {responseData.postData.dest}
                                                    </div>
                                                </div>
                                                <div className="mfr-modal-detail-item">
                                                    <div className="mfr-modal-detail-label">Aircraft</div>
                                                    <div className="mfr-modal-detail-value">
                                                        {responseData.acmap || responseData.postData.eqpt}
                                                    </div>
                                                </div>
                                                {/* <div className="mfr-modal-detail-item">
                                                    <div className="mfr-modal-detail-label">Flight Time</div>
                                                    <div className="mfr-modal-detail-value">
                                                        {formatFlightTime(responseData.postData.hours, responseData.postData.mins)}
                                                    </div>
                                                </div> */}
                                                <div className="mfr-modal-detail-item">
                                                    <div className="mfr-modal-detail-label">Status</div>
                                                    <div className="mfr-modal-detail-value" style={{color: '#f59e0b'}}>
                                                        {responseData.info?.logstatus?.toUpperCase() || 'PENDING'}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Performance Metrics */}
                                    {responseData.info && (
                                        <div className="mfr-modal-details">
                                            <h3 className="mfr-modal-details-title">
                                                <span role="img" aria-hidden="true">📊</span>
                                                Performance Metrics
                                            </h3>
                                            <div className="mfr-modal-details-grid">
                                                {responseData.info.airtime && (
                                                    <div className="mfr-modal-detail-item">
                                                        <div className="mfr-modal-detail-label">Block Time</div>
                                                        <div className="mfr-modal-detail-value">
                                                            {responseData.info.blocktime.toFixed(1)}h
                                                            
                                                        </div>
                                                    </div>
                                                )}
                                                {responseData.info.blocktime && (
                                                    <div className="mfr-modal-detail-item">
                                                        <div className="mfr-modal-detail-label">Enrte Time</div>
                                                        <div className="mfr-modal-detail-value">
                                                            {responseData.info.airtime.toFixed(1)}h
                                                        </div>
                                                    </div>
                                                )}
                                                {responseData.info.landingFPM && (
                                                    <div className="mfr-modal-detail-item">
                                                        <div className="mfr-modal-detail-label">Landing</div>
                                                        <div className="mfr-modal-detail-value">
                                                            {responseData.info.landingFPM} FPM
                                                        </div>
                                                    </div>
                                                )}
                                                {responseData.payload && (
                                                    <div className="mfr-modal-detail-item">
                                                        <div className="mfr-modal-detail-label">Payload</div>
                                                        <div className="mfr-modal-detail-value">
                                                            {responseData.payload.toLocaleString()} lbs
                                                        </div>
                                                    </div>
                                                )}
                                                {responseData.info.blockfuel && (
                                                    <div className="mfr-modal-detail-item">
                                                        <div className="mfr-modal-detail-label">Block Fuel</div>
                                                        <div className="mfr-modal-detail-value">
                                                            {responseData.info.blockfuel.toLocaleString()} lbs
                                                        </div>
                                                    </div>
                                                )}
                                                {responseData.takeoffFR && (
                                                    <div className="mfr-modal-detail-item">
                                                        <div className="mfr-modal-detail-label">Flight Rules</div>
                                                        <div className="mfr-modal-detail-value">
                                                            {responseData.takeoffFR}/{responseData.landingFR}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {/* Financial Report */}
                                    {responseData.info?.report && (
                                        <div className="mfr-modal-details">
                                            <h3 className="mfr-modal-details-title">
                                                <span role="img" aria-hidden="true">💰</span>
                                                Financial Summary
                                            </h3>
                                            <pre className="mfr-modal-report">
                                                {responseData.info.report}
                                            </pre>
                                        </div>
                                    )}

                                    {/* Airport Information */}
                                    {responseData.airportData && (
                                        <div className="mfr-modal-airports">
                                            {responseData.airportData.orig && (
                                                <div className="mfr-modal-airport">
                                                    <h4 className="mfr-modal-airport-code">
                                                        🛫 {responseData.airportData.orig.ident}
                                                    </h4>
                                                    <p className="mfr-modal-airport-name">
                                                        {responseData.airportData.orig.name}
                                                        <br />
                                                        <small>{responseData.airportData.orig.municipality}, {responseData.airportData.orig.iso_region}</small>
                                                    </p>
                                                </div>
                                            )}
                                            {responseData.airportData.dest && (
                                                <div className="mfr-modal-airport">
                                                    <h4 className="mfr-modal-airport-code">
                                                        🛬 {responseData.airportData.dest.ident}
                                                    </h4>
                                                    <p className="mfr-modal-airport-name">
                                                        {responseData.airportData.dest.name}
                                                        <br />
                                                        <small>{responseData.airportData.dest.municipality}, {responseData.airportData.dest.iso_region}</small>
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        {/* Modal Footer */}
                        <div className="mfr-modal-footer">
                            <button
                                type="button"
                                className="mfr-modal-btn"
                                onClick={handleModalClose}
                                tabIndex={9}
                                autoFocus
                            >
                                <span role="img" aria-hidden="true">↩️</span>
                                Return to Operations
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Visually hidden styles for accessibility */}
            <style>{`
                .visually-hidden {
                    position: absolute;
                    width: 1px;
                    height: 1px;
                    padding: 0;
                    margin: -1px;
                    overflow: hidden;
                    clip: rect(0, 0, 0, 0);
                    white-space: nowrap;
                    border: 0;
                }
            `}</style>
        </div>
    );
};

export default ManualFlightReportPage;
